import React, { useRef, useEffect, useCallback, useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core";
import { useSwipeable } from 'react-swipeable';
import CircularProgress from '@material-ui/core/CircularProgress';

const Styles = makeStyles((theme: Theme) =>
    createStyles({
        backdrop: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.9)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            outline: 'none',
            touchAction: 'pan-y',
        },
        imageContainer: {
            position: 'relative',
            maxWidth: '100%',
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        image: {
            maxHeight: '90vh',
            maxWidth: '90vw',
            touchAction: 'manipulation',
        },
        closeButton: {
            position: 'absolute',
            top: theme.spacing(2),
            right: theme.spacing(2),
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '2rem',
            cursor: 'pointer',
            zIndex: 1500,
        },
        navButton: {
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(0,0,0,0.5)',
            border: 'none',
            color: 'white',
            fontSize: '2rem',
            cursor: 'pointer',
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1500,
        },
        prevButton: {
            left: theme.spacing(2),
        },
        nextButton: {
            right: theme.spacing(2),
        },
        caption: {
            color: 'white',
            marginTop: theme.spacing(2),
            textAlign: 'center',
            maxWidth: '80vw',
            fontSize: '1rem',
            lineHeight: 1.4,
        },
        counter: {
            position: 'absolute',
            bottom: theme.spacing(2),
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'white',
            background: 'rgba(0,0,0,0.5)',
            padding: '5px 15px',
            borderRadius: '20px',
            fontSize: '0.9rem',
        },
        loadingContainer: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        progress: {
            color: 'white',
            marginBottom: theme.spacing(2),
        },
    })
);

// Utility to safely merge multiple refs
function mergeRefs<T = any>(...refs: React.Ref<T>[]): React.RefCallback<T> {
    return (node: T) => {
        refs.forEach(ref => {
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref && typeof ref === 'object' && 'current' in ref) {
                (ref as React.MutableRefObject<T | null>).current = node;
            }
        });
    };
}

export const AccessibleImageLightBox = (props: {
    images: string[];
    captions?: string[];
    isOpen: boolean;
    photoIndex: number;
    setPhotoIndex: React.Dispatch<React.SetStateAction<number>>;
    handleClose: () => void;
}) => {
    const classes = Styles();
    const lightboxRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const [isLoading, setIsLoading] = useState(true);

    const handlers = useSwipeable({
        onSwipedLeft: () => {
            props.setPhotoIndex((props.photoIndex + 1) % props.images.length);
        },
        onSwipedRight: () => {
            props.setPhotoIndex((props.photoIndex - 1 + props.images.length) % props.images.length);
        },
        delta: 15,
        trackTouch: true,
        trackMouse: true,
    });

    const combinedRef = mergeRefs(lightboxRef, handlers.ref);

    const handleImageLoad = () => {
        setIsLoading(false);
    };

    const handleImageChange = useCallback(() => {
        if (imageRef.current) {
            setIsLoading(true);
            imageRef.current.onload = handleImageLoad;
            imageRef.current.src = props.images[props.photoIndex];
        }
    }, [props.photoIndex, props.images]);

    useEffect(() => {
        handleImageChange();
    }, [handleImageChange]);

    const getLightboxProps = (): React.HTMLProps<HTMLDivElement> => ({
        ...handlers,
        ref: combinedRef,
        role: "dialog",
        "aria-modal": "true",
        tabIndex: -1,
        className: classes.backdrop,
        onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Escape') props.handleClose();
            if (e.key === 'ArrowRight') {
                props.setPhotoIndex((props.photoIndex + 1) % props.images.length);
            }
            if (e.key === 'ArrowLeft') {
                props.setPhotoIndex((props.photoIndex - 1 + props.images.length) % props.images.length);
            }
        },
    });

    useEffect(() => {
        if (props.isOpen && lightboxRef.current) {
            lightboxRef.current.focus();
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [props.isOpen]);

    if (!props.isOpen) return null;

    return (
        <div {...getLightboxProps()}>
            <button
                onClick={props.handleClose}
                className={classes.closeButton}
                aria-label="Close lightbox"
            >
                ×
            </button>

            <div className={classes.imageContainer}>
                {isLoading && (
                    <div className={classes.loadingContainer}>
                        <CircularProgress className={classes.progress} size={30} />
                    </div>
                )}
                <img
                    ref={imageRef}
                    src={props.images[props.photoIndex]}
                    alt=""
                    className={classes.image}
                    draggable="false"
                    onLoad={handleImageLoad}
                    style={{ display: isLoading ? 'none' : 'block' }}
                />

                {props.captions?.[props.photoIndex] && (
                    <div className={classes.caption}>
                        {props.captions[props.photoIndex]}
                    </div>
                )}

                <div className={classes.counter}>
                    {props.photoIndex + 1} / {props.images.length}
                </div>
            </div>

            {props.images.length > 1 && (
                <>
                    <button
                        onClick={() =>
                            props.setPhotoIndex(
                                (props.photoIndex - 1 + props.images.length) % props.images.length
                            )
                        }
                        className={`${classes.navButton} ${classes.prevButton}`}
                        aria-label="Previous image"
                    >
                        ‹
                    </button>
                    <button
                        onClick={() =>
                            props.setPhotoIndex((props.photoIndex + 1) % props.images.length)
                        }
                        className={`${classes.navButton} ${classes.nextButton}`}
                        aria-label="Next image"
                    >
                        ›
                    </button>
                </>
            )}
        </div>
    );
};